import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  Ref,
  ChangeEvent,
  KeyboardEvent,
} from "react";
import "./style.css";
import { FaSearch } from "react-icons/fa";
import { Form } from "react-bootstrap";

interface Option {
  label: string;
  value: string;
  isDisabled?: boolean;
}

interface StyleFunctions {
  [key: string]: (
    defaultStyle: React.CSSProperties,
    props: { isDisabled: boolean; isOpen: boolean; selectedOption?: Option }
  ) => React.CSSProperties;
}

interface CustomSelectProps {
  iconBeforePlaceholder?: React.ReactNode;
  options?: Option[];
  placeholder?: string;
  PreDifplaceholder?: boolean;
  multiSelect?: boolean;
  defaultSelected?: Option[];
  maxSelection?: number;
  disabledOptions?: string[];
  value?: Option[];
  onChange?: (selected: Option[]) => void;
  searchable?: boolean;
  withSelectAll?: boolean;
  selectContainerClassName?: string;
  selectvalueClassName?: string;
  error?: string;
  inputType?: "checkbox" | "radio";
  label?: string;
  isDisabled?: boolean;
  isError?: boolean;
  styles?: StyleFunctions;
  selectedOption?: Option;
  inputContainerClassName?: string;
  arrowClassName?: string;
}

const CustomSelect = forwardRef<HTMLDivElement, CustomSelectProps>(
  (
    {
      options = [],
      value,
      placeholder = "",
      PreDifplaceholder = false,
      multiSelect = false,
      defaultSelected = [],
      maxSelection = Infinity,
      disabledOptions = [],
      onChange,
      searchable = true,
      withSelectAll = false,
      selectContainerClassName = "",
      selectvalueClassName = "",
      error = "",
      inputType = "checkbox",
      label = "",
      isDisabled = false,
      isError = false,
      styles = {},
      selectedOption,
      iconBeforePlaceholder = false,
      inputContainerClassName = "",
      arrowClassName = "",
    },
    ref: Ref<HTMLDivElement>
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedValues, setSelectedValues] =
      useState<Option[]>(defaultSelected);
    const [search, setSearch] = useState("");
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(e.target as Node)
        ) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
      if (value !== undefined) {
        setSelectedValues(value);
      }
    }, [value]);

    const toggleDropdown = () => {
      setSearch("");
      if (!isDisabled) setIsOpen(!isOpen);
    };

    const handleSelect = (option: Option) => {
      if (disabledOptions.includes(option.value)) return;

      let updatedSelection: Option[] = [];

      if (inputType === "radio") {
        updatedSelection = [option];
      } else if (multiSelect) {
        if (selectedValues.some((val) => val.value === option.value)) {
          updatedSelection = selectedValues.filter(
            (val) => val.value !== option.value
          );
        } else if (selectedValues.length < maxSelection) {
          updatedSelection = [...selectedValues, option];
        } else {
          updatedSelection = selectedValues;
        }
      } else {
        updatedSelection = [option];
        setIsOpen(false);
      }

      setSelectedValues(updatedSelection);
      onChange && onChange(updatedSelection);
    };

    const filteredOptions = Array.isArray(options)
      ? options.filter(
          (option) =>
            typeof option.label === "string" &&
            option.label.toLowerCase().includes(search.toLowerCase())
        )
      : [];

    const handleSelectAll = () => {
      let newSelections: Option[] = [];

      if (selectedValues.length === filteredOptions.length) {
        newSelections = [];
      } else {
        newSelections = filteredOptions.filter(
          (opt) => !disabledOptions.includes(opt.value)
        );
      }

      setSelectedValues(newSelections);
      onChange && onChange(newSelections);
    };

    const handleOptionClick = (option: Option) => {
      if (option.value === "select-all") {
        handleSelectAll();
      } else {
        handleSelect(option);
      }
    };

    const displayPlaceholder =
      selectedValues.length === 0
        ? placeholder || "Select"
        : selectedValues.length === 1
          ? selectedValues[0].label
          : `${selectedValues.length} Selected`;

    const isAllSelected =
      Array.isArray(filteredOptions) &&
      Array.isArray(selectedValues) &&
      filteredOptions.every((opt) =>
        selectedValues.some((val) => val?.value === opt?.value)
      );

    const selectAllLabel = isAllSelected ? "Unselect all" : "Select all";

    const optionsToDisplay =
      withSelectAll &&
      multiSelect &&
      options.length > 1 &&
      inputType !== "radio" &&
      filteredOptions.length > 0
        ? [{ label: selectAllLabel, value: "select-all" }, ...filteredOptions]
        : filteredOptions;

    const handleOptionClickWrapper = (option: Option) => () =>
      handleOptionClick(option);

    const handleKeyDownWrapper = (option: Option) => (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        handleOptionClick(option);
      }
    };

    const getStyle = (
      key: string,
      defaultStyles?: React.CSSProperties
    ): React.CSSProperties | undefined => {
      return styles[key]
        ? styles[key](defaultStyles || {}, {
            isDisabled,
            isOpen,
            selectedOption,
          })
        : defaultStyles;
    };

    return (
      <div
        className={`custom-select ${selectContainerClassName} ${isDisabled ? "disabled" : ""}`}
        ref={dropdownRef}
      >
        <div className="select-head">
          {label && <label className="select-label">{label}</label>}
          {error && <div className="select-error">{error}</div>}
        </div>

        <div
          className={`select-input ${isOpen ? "openDrop" : ""} ${
            isDisabled ? "disabled-input" : ""
          } ${isError ? "errorinput" : ""} ${inputContainerClassName}`}
          onClick={toggleDropdown}
          style={selectedOption ? getStyle("control") : undefined}
          role="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          tabIndex={isDisabled ? -1 : 0}
          ref={ref}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleDropdown();
            }
          }}
        >
          <div
            className={`select-value ${selectvalueClassName} ${isDisabled ? "disabled" : ""}`}
            style={selectedOption ? getStyle("control") : undefined}
          >
            {iconBeforePlaceholder && (
              <span className="custom-placeholder-icon">
                {iconBeforePlaceholder}
              </span>
            )}
            {displayPlaceholder}
          </div>

          <div className="dropdown-border">
            {!isDisabled && (
              <div aria-hidden="true">
                {selectedOption ? (
                  <div
                    className={`alternatearrow ${isOpen ? "open" : ""}`}
                  ></div>
                ) : (
                  <div
                    className={`arrow ${isOpen ? "open" : ""} ${arrowClassName}`}
                  ></div>
                )}
              </div>
            )}
          </div>
        </div>

        {isOpen && !isDisabled && (
          <div className="dropdown menu-container">
            {searchable && (
              <div className="searchContainer">
                <Form.Control
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={"searchInput"}
                />
                <FaSearch className={"searchIcon"} />
              </div>
            )}
            <ul className="options-list custom_scroll">
              {Array.isArray(optionsToDisplay) &&
              optionsToDisplay.length > 0 ? (
                optionsToDisplay.map((option, index) => {
                  const isSelected = selectedValues.some(
                    (val) => val.value === option.value
                  );
                  const isOptionDisabled =
                    option.isDisabled || disabledOptions.includes(option.value);

                  return (
                    <li
                      key={index}
                      role="option"
                      aria-selected={isSelected}
                      tabIndex={0}
                      className={`option 
                        ${isSelected ? "selected" : ""} 
                        ${isOptionDisabled ? "disabled" : ""} 
                        ${option.value === "select-all" ? "selectAllOptionClass" : ""}`}
                      onClick={
                        !isOptionDisabled
                          ? handleOptionClickWrapper(option)
                          : undefined
                      }
                      onKeyDown={
                        !isOptionDisabled
                          ? handleKeyDownWrapper(option)
                          : undefined
                      }
                      style={getStyle("option")}
                    >
                      {multiSelect && (
                        <input
                          type={inputType}
                          readOnly
                          checked={isSelected}
                          disabled={isOptionDisabled}
                          className={`${
                            option.value === "select-all" ? "d-none" : ""
                          } ${isOptionDisabled ? "checkbox-disabled" : ""}`}
                        />
                      )}
                      <span
                        className={
                          option.value === "select-all"
                            ? "select-all-text"
                            : "option-text"
                        }
                      >
                        {option.label}
                      </span>
                    </li>
                  );
                })
              ) : (
                <li className="option disabled selected">No Results</li>
              )}
            </ul>
          </div>
        )}
        {error && <div className="error-message">{error}</div>}
      </div>
    );
  }
);

CustomSelect.displayName = "CustomSelect";
export default CustomSelect;
